import React, { useMemo, useState, useEffect } from "react";
import { IonButton, IonSpinner, IonIcon } from "@ionic/react";
import Avatar from "../../ui/Avatar";
import { getInitials, getUserName } from "../../../utils/helpers";
import { formatTimeAgoShort } from "../../../utils/dateHelpers";
import { Comment } from "../../../services/postsService";
import { closeCircleOutline } from "ionicons/icons";

interface CommentActionDialogProps {
  isOpen: boolean;
  comment: Comment | null;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: (commentId: number) => void;
  onEdit?: (commentId: number, content: string) => Promise<void> | void;
}

const CommentActionDialog: React.FC<CommentActionDialogProps> = ({
  isOpen,
  comment,
  isDeleting,
  onCancel,
  onConfirm,
  onEdit,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const originalContent = useMemo(
    () => comment?.content || "",
    [comment?.content],
  );
  const [value, setValue] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsEditing(false);
      setValue("");
      setShowDeleteAlert(false);
    }
  }, [isOpen, comment?.comment_id]);

  if (!isOpen || !comment) return null;
  const handleConfirm = () => {
    const idNum = Number(comment.comment_id);
    if (!Number.isNaN(idNum)) onConfirm(idNum);
  };

  const handleStartEdit = () => {
    setValue(originalContent);
    setIsEditing(true);
  };

  const handleUpdate = async () => {
    if (!onEdit) return;
    const idNum = Number(comment.comment_id);
    if (Number.isNaN(idNum)) return;
    const trimmed = (value || "").trim();
    if (!trimmed || trimmed === originalContent) return;
    try {
      setIsSaving(true);
      await onEdit(idNum, trimmed);
    } finally {
      setIsSaving(false);
      setIsEditing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/55 z-[14000] flex items-center justify-center p-5"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-[520px] shadow-2xl overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
      >
        <IonIcon
          onClick={onCancel}
          style={{
            position: "absolute",
            top: "15px",
            right: "20px",
            fontSize: "18px",
            color: "#05ACD9",
            cursor: "pointer",
            width: "26px",
            height: "26px",
          }}
          icon={closeCircleOutline}
        />
        {showDeleteAlert ? (
          <>
            <div className="px-8 pt-8 pb-2 text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Delete Comment
              </h3>
              <p className="text-gray-500 text-sm">
                Delete this comment permanently?
              </p>
            </div>
            <div className="w-full flex flex-col items-center gap-4 mb-2 px-4 pt-2 pb-0">
              <IonButton
                expand="block"
                onClick={handleConfirm}
                className="danger-button mt-0"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <span className="flex items-center justify-center gap-2">
                    <IonSpinner name="dots" style={{ width: 16, height: 16 }} />
                    Deleting...
                  </span>
                ) : (
                  "Yes, Delete"
                )}
              </IonButton>
              <IonButton
                expand="block"
                onClick={() => setShowDeleteAlert(false)}
                className="tertiary-button mt-0"
                // style={{ "--color": "#000", "--border-color": "#dadcdf" } as React.CSSProperties}
                disabled={isDeleting}
              >
                Cancel
              </IonButton>
            </div>
          </>
        ) : !isEditing ? (
          <>
            <div className="p-4 pb-3">
              <div className="flex gap-3">
                <Avatar
                  initials={getInitials(
                    comment.author?.first_name || "",
                    comment.author?.last_name || "",
                    comment.author?.email,
                  )}
                  imageSrc={comment.author?.avatar || undefined}
                  imageClassName="rounded-[12px] border"
                  innerClassName="avatar-inner-38"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">
                      {getUserName(comment.author)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatTimeAgoShort(new Date(comment.created_at))}
                    </span>
                  </div>
                  <div className="mt-1.5 text-gray-700 text-sm leading-relaxed">
                    {comment.content}
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full flex flex-col items-center gap-5 mb-2 px-4 pt-3 pb-4">
              <IonButton
                expand="block"
                onClick={handleStartEdit}
                className="primary-button"
              >
                Edit
              </IonButton>
              <IonButton
                expand="block"
                onClick={() => setShowDeleteAlert(true)}
                className="tertiary-button mt-0"
                // style={
                //   {
                //     "--color": "red",
                //     "--border-color": "red",
                //   } as React.CSSProperties
                // }
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <span className="flex items-center gap-2">
                    <IonSpinner name="dots" style={{ width: 16, height: 16 }} />
                    Deleting
                  </span>
                ) : (
                  "Delete"
                )}
              </IonButton>
            </div>
          </>
        ) : (
          <>
            <div className="p-4 pb-3">
              <div className="flex gap-3">
                <Avatar
                  initials={getInitials(
                    comment.author?.first_name || "",
                    comment.author?.last_name || "",
                    comment.author?.email,
                  )}
                  imageSrc={comment.author?.avatar || undefined}
                  imageClassName="rounded-[12px] border"
                  innerClassName="avatar-inner-38"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">
                      {getUserName(comment.author)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatTimeAgoShort(new Date(comment.created_at))}
                    </span>
                  </div>
                  <div className="mt-2">
                    <textarea
                      className="w-full rounded-xl border border-gray-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={2}
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      placeholder="Edit your comment"
                      disabled={isSaving}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full flex flex-col items-center gap-5 mb-4 px-4 pt-3 pb-4">
              <IonButton
                expand="block"
                className="primary-button"
                onClick={handleUpdate}
                disabled={
                  isSaving || !value.trim() || value.trim() === originalContent
                }
              >
                {isSaving ? (
                  <span className="flex items-center gap-2">
                    <IonSpinner name="dots" style={{ width: 16, height: 16 }} />
                    Updating
                  </span>
                ) : (
                  "Update"
                )}
              </IonButton>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CommentActionDialog;
